import { useState, useEffect, useMemo, useRef } from 'react';
import { X, Banknote, CreditCard, Tag, Trash2 } from 'lucide-react';
import { POS_Z_CLASS } from '../../utils/posZLayers';
import {
  montantFinal as calcMontantFinal,
  resteAPayer,
  sommeLignes,
  totalEspecesLignes,
  calcRenduEspeces,
  validerLignesPaiement,
  labelMethode,
  roundMoney,
} from '../../utils/caissePaiement';

const METHODS = [
  { id: 'ESPECES', label: 'Espèces', icon: Banknote },
  { id: 'CARTE', label: 'Carte', icon: CreditCard },
  { id: 'CHEQUE', label: 'Chèque', icon: CreditCard },
  { id: 'VIREMENT', label: 'Virement', icon: CreditCard },
];

let ligneId = 0;
const newLigneId = () => `pay-${++ligneId}-${Date.now()}`;

/**
 * L1-T6 — `montant` = solde restant dû (après déjà réglé).
 * `totalAddition` / `dejaRegle` / `historiquePaiements` = affichage contexte.
 */
export default function CaissePaymentModal({
  open,
  title,
  subtitle,
  contextNote = '',
  montant,
  sousTotalArticles = null,
  reduction = 0,
  reductionAffichee = 0,
  totalAddition = null,
  dejaRegle = 0,
  fraisLivraison = 0,
  fraisExpedition = 0,
  historiquePaiements = [],
  onRefreshFromServer,
  fmt,
  codePromo = '',
  setCodePromo,
  onVerifierPromo,
  promoMessage = '',
  promoLoading = false,
  showCodePromo = false,
  error = '',
  onClose,
  onConfirm,
  processing,
}) {
  const [lignes, setLignes] = useState([]);
  const [methode, setMethode] = useState('ESPECES');
  const [montantSaisi, setMontantSaisi] = useState('');
  const [montantRecuEspeces, setMontantRecuEspeces] = useState('');
  const [localHint, setLocalHint] = useState('');
  const montantSaisiDirtyRef = useRef(false);
  const prevOpenRef = useRef(false);
  const prevDuRef = useRef(null);

  /** `montant` = solde exigible serveur (montantDu) — jamais re-soustraire la réduction. */
  const du = montant != null ? roundMoney(montant) : calcMontantFinal(montant, reduction);
  const reste = du != null ? resteAPayer(du, lignes) : 0;
  const prepareTotal = sommeLignes(lignes);
  const resteApresPrepare =
    du != null ? roundMoney(Math.max(0, du - prepareTotal)) : 0;
  const especesTotal = totalEspecesLignes(lignes);
  const rendu = calcRenduEspeces(montantRecuEspeces, lignes);
  const soldeOk = reste <= 0.001 && lignes.length > 0 && prepareTotal > 0;
  const hasOperation = lignes.length > 0;
  const isPartialOperation = hasOperation && reste > 0.001;

  const checkFull = useMemo(
    () =>
      du != null
        ? validerLignesPaiement(du, lignes, montantRecuEspeces, { allowPartial: false })
        : { ok: false, message: '' },
    [du, lignes, montantRecuEspeces]
  );

  const checkPartial = useMemo(
    () =>
      du != null
        ? validerLignesPaiement(du, lignes, montantRecuEspeces, { allowPartial: true })
        : { ok: false, message: '' },
    [du, lignes, montantRecuEspeces]
  );

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      prevDuRef.current = null;
      montantSaisiDirtyRef.current = false;
      return;
    }
    if (!prevOpenRef.current) {
      setLignes([]);
      setMethode('ESPECES');
      setMontantSaisi('');
      setMontantRecuEspeces('');
      setLocalHint('');
      montantSaisiDirtyRef.current = false;
    }
    prevOpenRef.current = true;
  }, [open]);

  useEffect(() => {
    if (!open || du == null) return;
    if (
      prevDuRef.current != null &&
      Math.abs(prevDuRef.current - du) > 0.001 &&
      lignes.length > 0
    ) {
      setLignes([]);
      setMontantRecuEspeces('');
      setLocalHint('Montant serveur actualisé — paiements préparés réinitialisés.');
      montantSaisiDirtyRef.current = false;
    }
    prevDuRef.current = du;
  }, [open, du, lignes.length]);

  useEffect(() => {
    if (!open || du == null || reste <= 0.001) return;
    if (montantSaisiDirtyRef.current) return;
    setMontantSaisi(String(reste));
  }, [open, du, reste]);

  useEffect(() => {
    if (especesTotal > 0 && !montantRecuEspeces) {
      setMontantRecuEspeces(String(especesTotal));
    }
  }, [especesTotal, montantRecuEspeces]);

  if (!open) return null;

  const supprimerLigne = (id) => {
    setLignes((prev) => prev.filter((l) => l.id !== id));
    setLocalHint('');
  };

  const enregistrerPaiementCourant = () => {
    const m = roundMoney(montantSaisi);
    if (!m || m <= 0) {
      return { ok: false, message: 'Indiquez un montant supérieur à 0' };
    }
    if (m > reste + 0.001) {
      return { ok: false, message: `Maximum ${fmt(reste)} (reste à payer)` };
    }
    const newLignes = [...lignes, { id: newLigneId(), methode, montant: m }];
    const nouveauReste = roundMoney(reste - m);
    setLignes(newLignes);
    montantSaisiDirtyRef.current = false;
    setMontantSaisi(nouveauReste > 0 ? String(nouveauReste) : '');
    if (nouveauReste > 0.001) {
      setMethode(methode === 'ESPECES' ? 'CARTE' : methode);
    }
    return { ok: true, lignes: newLignes, nouveauReste };
  };

  const confirmWith = (finalCheck) => {
    if (!finalCheck.ok) {
      setLocalHint(finalCheck.message);
      return;
    }
    if (!processing) onConfirm?.(finalCheck);
  };

  const handleValider = () => {
    setLocalHint('');

    if (!soldeOk) {
      const added = enregistrerPaiementCourant();
      if (!added.ok) {
        setLocalHint(added.message);
        return;
      }
      if (added.nouveauReste > 0.001) {
        // Ligne ajoutée — rester ouvert pour mixte ou encaisser partiel
        return;
      }
      const finalCheck = validerLignesPaiement(du, added.lignes, montantRecuEspeces, {
        allowPartial: false,
      });
      confirmWith(finalCheck);
      return;
    }

    confirmWith(checkFull);
  };

  const handleEncaisserPartiel = () => {
    setLocalHint('');
    let working = lignes;
    if (!soldeOk && montantSaisi !== '' && roundMoney(montantSaisi) > 0) {
      const added = enregistrerPaiementCourant();
      if (!added.ok) {
        setLocalHint(added.message);
        return;
      }
      working = added.lignes;
    }
    if (!working.length) {
      setLocalHint('Ajoutez au moins un règlement');
      return;
    }
    const finalCheck = validerLignesPaiement(du, working, montantRecuEspeces, {
      allowPartial: true,
    });
    if (!finalCheck.isPartial && finalCheck.ok) {
      confirmWith(finalCheck);
      return;
    }
    confirmWith(finalCheck);
  };

  const montantCourantValide =
    montantSaisi !== '' &&
    roundMoney(montantSaisi) > 0 &&
    roundMoney(montantSaisi) <= reste + 0.001;

  const peutValider = soldeOk
    ? checkFull.ok && !processing
    : montantCourantValide && !processing;

  const peutPartiel =
    !processing &&
    ((hasOperation && isPartialOperation && checkPartial.ok) ||
      (!soldeOk && montantCourantValide && roundMoney(montantSaisi) < (du || 0) - 0.001));

  const libelleValider = processing
    ? '…'
    : soldeOk
      ? 'Terminer la vente'
      : `Valider · ${fmt(roundMoney(montantSaisi) || 0)} ${labelMethode(methode).toLowerCase()}`;

  const totalTtcNet =
    totalAddition != null ? roundMoney(totalAddition) : du != null ? roundMoney(du + (dejaRegle || 0)) : null;
  const sousTotalVal =
    sousTotalArticles != null && Number(sousTotalArticles) > 0
      ? roundMoney(sousTotalArticles)
      : null;

  return (
    <div
      className={`fixed inset-0 ${POS_Z_CLASS.modalBackdrop} flex items-end sm:items-center justify-center p-0 sm:p-4 pb-[env(safe-area-inset-bottom,0px)] bg-slate-900/60 backdrop-blur-sm`}
    >
      <div className="pos-caisse-pay-modal-panel bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[min(92dvh,720px)] flex flex-col mx-[max(0px,env(safe-area-inset-left))] mr-[max(0px,env(safe-area-inset-right))]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{subtitle}</p>}
            {contextNote ? (
              <p
                className="text-xs text-violet-800 bg-violet-50 border border-violet-100 rounded-lg px-2 py-1.5 mt-2"
                data-testid="caisse-payment-cross-post-note"
              >
                {contextNote}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {showCodePromo && setCodePromo && (
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5 mb-1.5">
                <Tag className="h-4 w-4" /> Code promo
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={codePromo}
                  onChange={(e) => setCodePromo(e.target.value.toUpperCase())}
                  placeholder="PROMO2025"
                  className="flex-1 border-2 border-slate-200 rounded-xl px-3 py-2.5 uppercase focus:border-emerald-500 outline-none"
                />
                {onVerifierPromo && (
                  <button
                    type="button"
                    onClick={onVerifierPromo}
                    disabled={promoLoading || !codePromo.trim()}
                    className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
                  >
                    {promoLoading ? '…' : 'OK'}
                  </button>
                )}
              </div>
              {promoMessage && (
                <p
                  className={`text-xs mt-1.5 ${
                    promoMessage.includes('Réduction') ? 'text-emerald-700 font-medium' : 'text-red-600'
                  }`}
                >
                  {promoMessage}
                </p>
              )}
            </div>
          )}

          <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 mb-4 space-y-1.5">
            {sousTotalVal != null && (
              <div className="flex justify-between text-sm text-slate-600">
                <span>Sous-total articles</span>
                <span className="font-semibold text-slate-900">{fmt(sousTotalVal)}</span>
              </div>
            )}
            {Number(reductionAffichee) > 0 && (
              <div className="flex justify-between text-sm text-emerald-700">
                <span>Réductions</span>
                <span className="font-semibold">−{fmt(reductionAffichee)}</span>
              </div>
            )}
            {Number(fraisLivraison) > 0 && (
              <div className="flex justify-between text-sm text-slate-600">
                <span>Frais livraison</span>
                <span className="font-semibold">{fmt(fraisLivraison)}</span>
              </div>
            )}
            {Number(fraisExpedition) > 0 && (
              <div className="flex justify-between text-sm text-slate-600">
                <span>Frais expédition</span>
                <span className="font-semibold">{fmt(fraisExpedition)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-slate-800">
              <span>Total TTC commande</span>
              <span className="font-semibold text-slate-900">
                {totalTtcNet != null ? fmt(totalTtcNet) : '—'}
              </span>
            </div>
            <div className="flex justify-between text-sm text-slate-600">
              <span>Déjà encaissé</span>
              <span className="font-semibold text-emerald-700">{fmt(dejaRegle || 0)}</span>
            </div>
            <div className="flex justify-between text-sm text-slate-600">
              <span>Solde exigible</span>
              <span className="font-semibold text-slate-900">{du != null ? fmt(du) : '—'}</span>
            </div>
            {prepareTotal > 0 && (
              <div className="flex justify-between text-sm text-violet-700">
                <span>Paiements préparés (non validés)</span>
                <span className="font-semibold">{fmt(prepareTotal)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-1 border-t border-slate-200">
              <span className="text-sm font-medium text-slate-700">
                {lignes.length > 0 ? 'Reste après préparation' : 'Reste à payer'}
              </span>
              <span className="text-2xl font-bold text-amber-700">
                {du != null ? fmt(lignes.length > 0 ? resteApresPrepare : Math.max(0, reste)) : '—'}
              </span>
            </div>
          </div>

          {historiquePaiements?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Règlements déjà enregistrés
              </p>
              <ul className="space-y-1.5">
                {historiquePaiements.map((p) => (
                  <li
                    key={p.id || `${p.methodePaiement}-${p.montant}`}
                    className="flex justify-between text-sm bg-white border border-slate-100 rounded-lg px-3 py-2"
                  >
                    <span className="text-slate-700">{labelMethode(p.methodePaiement || p.methode)}</span>
                    <span className="font-medium text-slate-900">{fmt(Number(p.montant) || 0)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lignes.length > 0 && (
            <ul className="mt-2 space-y-2">
              {lignes.map((l) => (
                <li
                  key={l.id}
                  className="flex items-center gap-2 bg-emerald-50/60 rounded-xl px-3 py-2.5 border border-emerald-100"
                >
                  <span className="flex-1 text-sm font-medium text-slate-800">
                    {labelMethode(l.methode)}
                  </span>
                  <span className="font-bold text-slate-900">{fmt(l.montant)}</span>
                  <button
                    type="button"
                    onClick={() => supprimerLigne(l.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                    title="Retirer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {soldeOk && (
            <p className="mt-3 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 border border-emerald-100">
              ✓ Solde entièrement couvert — finalisation de la vente
            </p>
          )}

          {!soldeOk && du != null && (
            <>
              <p className="text-sm text-slate-600 mt-4 mb-2">
                Saisissez un montant (tout ou partie), puis validez — ou encaisser partiel pour garder le ticket ouvert.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setMethode(id);
                      setMontantSaisi(String(reste));
                    }}
                    className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition ${
                      methode === id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 bg-slate-50 text-slate-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={reste}
                  value={montantSaisi}
                  onChange={(e) => {
                    montantSaisiDirtyRef.current = true;
                    setMontantSaisi(e.target.value);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && peutValider && handleValider()}
                  className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-3 text-lg focus:border-emerald-500 outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    montantSaisiDirtyRef.current = false;
                    setMontantSaisi(String(reste));
                  }}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 shrink-0"
                >
                  Tout
                </button>
              </div>
              {methode === 'CARTE' && (
                <p className="text-xs text-slate-500 mt-2">
                  Carte = règlement déclaré sur TPE externe (pas de confirmation bancaire Clickorico).
                </p>
              )}
            </>
          )}

          {(soldeOk || especesTotal > 0) && especesTotal > 0 && (
            <div className="mt-4 space-y-2 p-4 rounded-xl bg-emerald-50/80 border border-emerald-100">
              <p className="text-sm font-medium text-slate-700">
                Espèces reçues du client
                <span className="text-slate-500 font-normal">
                  {' '}
                  (part espèces {fmt(especesTotal)})
                </span>
              </p>
              <input
                type="number"
                min="0"
                step="0.01"
                value={montantRecuEspeces}
                onChange={(e) => setMontantRecuEspeces(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && peutValider && handleValider()}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-lg bg-white focus:border-emerald-500 outline-none"
              />
              {rendu != null && (
                <p className="text-sm font-semibold text-emerald-700">Rendu : {fmt(rendu)}</p>
              )}
            </div>
          )}

          {localHint && (
            <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              {localHint}
            </p>
          )}
        </div>

        {error && (
          <div className="mx-6 mb-0 shrink-0 space-y-2">
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
              {error}
            </p>
            {onRefreshFromServer && (
              <button
                type="button"
                onClick={() => onRefreshFromServer()}
                className="w-full text-sm font-semibold text-violet-700 bg-violet-50 border border-violet-100 rounded-xl px-4 py-2 hover:bg-violet-100"
              >
                Actualiser les montants depuis le serveur
              </button>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2 px-6 py-4 bg-slate-50 border-t border-slate-100 shrink-0">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3.5 rounded-xl border border-slate-300 font-medium text-slate-700 hover:bg-white"
            >
              Annuler
            </button>
            <button
              type="button"
              data-testid="caisse-payment-confirm"
              onClick={handleValider}
              disabled={!peutValider}
              className="flex-1 py-3.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-emerald-600/25 text-sm sm:text-base"
            >
              {libelleValider}
            </button>
          </div>
          {peutPartiel && (
            <button
              type="button"
              data-testid="caisse-payment-partial"
              onClick={handleEncaisserPartiel}
              disabled={!peutPartiel}
              className="w-full py-3 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-900 font-semibold hover:bg-amber-100 disabled:opacity-40 text-sm"
            >
              Encaisser partiel (garder le ticket ouvert)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
